import React, { useEffect, useMemo, useState } from "react";
import { api, money } from "../../lib/api";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Repeat,
  Moon,
  Wallet,
  TrendingUp,
  MapPin,
  Loader2,
  Send,
} from "lucide-react";
import DataTable from "../../components/admin/DataTable";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const RANGE_OPTIONS = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 180, label: "Last 6 months" },
  { value: 365, label: "Last 12 months" },
];

export default function AdminCustomers() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reengageOpen, setReengageOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get(`/admin/customers/insights?days=${days}`)
      .then(({ data }) => alive && setData(data))
      .catch((e) => alive && toast.error(e?.response?.data?.detail || "Failed to load insights"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [days]);

  const s = data?.summary || {};

  return (
    <div data-testid="admin-customers-page" className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="overline mb-2">Insights</div>
          <h1 className="font-display text-3xl md:text-4xl">Customer Insights</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-2 max-w-2xl">
            Who shops at your store, how often they return, what they spend, and where they're located —
            all scoped strictly to this tenant.
          </p>
        </div>
        <select
          className="kb-input md:w-48"
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value, 10))}
          data-testid="customers-range-select"
        >
          {RANGE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-[hsl(var(--kb-muted))]" data-testid="customers-loading">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading insights…
        </div>
      ) : !data ? (
        <div className="py-24 text-center text-[hsl(var(--kb-muted))]">No data</div>
      ) : (
        <>
          {/* Headline KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="customers-summary">
            <Kpi icon={Users} label="Total customers" value={s.total_customers ?? 0} testid="kpi-total" />
            <Kpi icon={UserPlus} label={`New (last ${s.days || days}d)`} value={s.new_customers ?? 0} testid="kpi-new" />
            <Kpi icon={Repeat} label="Repeat purchase rate" value={`${s.repeat_purchase_rate ?? 0}%`} testid="kpi-repeat" />
            <Kpi icon={Moon} label="Dormant > 30d" value={s.dormant_count ?? 0} testid="kpi-dormant" />
            <Kpi icon={Users} label="Returning customers" value={s.returning_customers ?? 0} testid="kpi-returning" />
            <Kpi icon={Wallet} label="Paying customers" value={s.paying_customers ?? 0} testid="kpi-paying" />
            <Kpi icon={TrendingUp} label="Avg lifetime value" value={money(s.avg_lifetime_value ?? 0)} testid="kpi-avg-ltv" />
            <Kpi icon={Wallet} label="Total LTV" value={money(s.total_lifetime_value ?? 0)} testid="kpi-total-ltv" />
          </div>

          {/* Charts row */}
          <div className="grid md:grid-cols-2 gap-6">
            <Panel title="Signup trend" testid="signup-trend-panel">
              <TrendChart points={data.signup_trend || []} />
            </Panel>
            <Panel title="Lifetime value distribution" testid="ltv-dist-panel">
              <LtvDistribution rows={data.ltv_distribution || []} />
            </Panel>
          </div>

          {/* Top spenders + Dormant */}
          <div className="grid lg:grid-cols-2 gap-6">
            <Panel title="Top spenders" testid="top-spenders-panel">
              <DataTable
                testid="top-spenders-table"
                columns={[
                  { key: "name", label: "Name", render: (r) => r.name || "—" },
                  { key: "phone", label: "Phone" },
                  { key: "orders", label: "Orders" },
                  { key: "spent", label: "Spent", render: (r) => money(r.spent) },
                ]}
                rows={data.top_spenders || []}
                emptyLabel="No paying customers yet"
              />
            </Panel>
            <Panel
              title="Dormant customers"
              testid="dormant-panel"
              action={
                <button
                  type="button"
                  onClick={() => setReengageOpen(true)}
                  disabled={!(s.dormant_count > 0)}
                  className="kb-btn-primary !py-2 !px-3 !text-xs inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="reengage-open-btn"
                  title={s.dormant_count > 0 ? "Send a push to all dormant customers" : "No dormant customers"}
                >
                  <Send size={12} />
                  Re-engage {s.dormant_count ? `(${s.dormant_count})` : ""}
                </button>
              }
            >
              <DataTable
                testid="dormant-table"
                columns={[
                  { key: "name", label: "Name", render: (r) => r.name || "—" },
                  { key: "phone", label: "Phone" },
                  { key: "days_since_last_order", label: "Days since", render: (r) => `${r.days_since_last_order}d` },
                  { key: "spent", label: "Lifetime", render: (r) => money(r.spent) },
                ]}
                rows={data.dormant || []}
                emptyLabel="No dormant customers — nice!"
              />
            </Panel>
          </div>

          {/* Geographic distribution */}
          <Panel
            title={<span className="inline-flex items-center gap-2"><MapPin size={16} /> Top cities</span>}
            testid="geo-panel"
          >
            <GeoTable rows={data.geo_distribution || []} />
          </Panel>
        </>
      )}

      <ReengageDialog
        open={reengageOpen}
        onOpenChange={setReengageOpen}
        dormantCount={s.dormant_count || 0}
        onSent={() => {
          // Refresh insights so the dormant count is up to date.
          api.get(`/admin/customers/insights?days=${days}`).then(({ data }) => setData(data)).catch(() => {});
        }}
      />
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div
      className="bg-white border border-[hsl(var(--kb-border))] p-4 flex flex-col gap-2"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-[hsl(var(--kb-muted))] text-xs uppercase tracking-wider">
        <Icon size={14} />
        {label}
      </div>
      <div className="text-2xl font-display">{value}</div>
    </div>
  );
}

function Panel({ title, action, children, testid }) {
  return (
    <section className="bg-white border border-[hsl(var(--kb-border))]" data-testid={testid}>
      <div className="px-5 py-3 border-b border-[hsl(var(--kb-border))] flex items-center justify-between gap-3">
        <div className="font-medium">{title}</div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function TrendChart({ points }) {
  const max = useMemo(() => Math.max(1, ...points.map((p) => p.signups)), [points]);
  if (!points.length) {
    return <div className="text-sm text-[hsl(var(--kb-muted))]">No signups in this window.</div>;
  }
  return (
    <div className="flex items-end gap-1 h-40" data-testid="signup-trend-chart">
      {points.map((p) => {
        const h = (p.signups / max) * 100;
        return (
          <div
            key={p.date}
            className="flex-1 flex flex-col items-center gap-1 group"
            title={`${p.date}: ${p.signups}`}
          >
            <div
              className="w-full bg-[hsl(var(--kb-primary))]/80 group-hover:bg-[hsl(var(--kb-primary))] transition-colors"
              style={{ height: `${Math.max(h, 2)}%`, minHeight: 2 }}
            />
          </div>
        );
      })}
    </div>
  );
}

function LtvDistribution({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="space-y-3" data-testid="ltv-distribution-chart">
      {rows.map((r) => {
        const pct = (r.count / max) * 100;
        return (
          <div key={r.range} className="flex items-center gap-3 text-sm">
            <div className="w-16 text-[hsl(var(--kb-muted))] text-xs font-mono">{r.range}</div>
            <div className="flex-1 bg-[hsl(var(--kb-surface-alt))] h-3">
              <div
                className="h-3 bg-[hsl(var(--kb-primary))]"
                style={{ width: `${Math.max(pct, 1)}%` }}
              />
            </div>
            <div className="w-10 text-right font-medium">{r.count}</div>
          </div>
        );
      })}
    </div>
  );
}

function GeoTable({ rows }) {
  if (!rows.length) {
    return <div className="text-sm text-[hsl(var(--kb-muted))]">No paid orders with city data yet.</div>;
  }
  return (
    <DataTable
      testid="geo-table"
      columns={[
        { key: "city", label: "City" },
        { key: "customers", label: "Unique customers" },
        { key: "orders", label: "Paid orders" },
        { key: "revenue", label: "Revenue", render: (r) => money(r.revenue) },
      ]}
      rows={rows}
      emptyLabel="No city data"
    />
  );
}

function ReengageDialog({ open, onOpenChange, dormantCount, onSent }) {
  const [title, setTitle] = useState("We miss you ✨");
  const [body, setBody] = useState("Fresh arrivals & a little something just for you — come take a look.");
  const [url, setUrl] = useState("/shop?sort=new");
  const [previewCount, setPreviewCount] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPreviewCount(null);
    api.get("/admin/customers/reengage/preview")
      .then(({ data }) => setPreviewCount(data.user_count ?? 0))
      .catch(() => setPreviewCount(dormantCount));
  }, [open, dormantCount]);

  const send = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/customers/reengage", {
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || "/",
      });
      toast.success(`Sent to ${data.sent} of ${data.attempted} dormant customers`);
      onOpenChange(false);
      onSent && onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reengage-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Re-engage dormant customers</DialogTitle>
          <DialogDescription>
            Sends a single push notification to every customer of this store whose last paid order was more than 30 days ago.
          </DialogDescription>
        </DialogHeader>

        <div className="text-sm bg-[hsl(var(--kb-surface-alt))] px-3 py-2 border border-[hsl(var(--kb-border))]" data-testid="reengage-preview-count">
          Will deliver to{" "}
          <span className="font-medium">{previewCount === null ? "…" : previewCount}</span>{" "}
          dormant {previewCount === 1 ? "customer" : "customers"} in this tenant.
        </div>

        <div className="space-y-3 mt-3">
          <label className="block text-xs">
            <div className="mb-1 text-[hsl(var(--kb-muted))] uppercase tracking-wider">Notification title</div>
            <input
              className="kb-input"
              maxLength={120}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="reengage-title-input"
            />
          </label>
          <label className="block text-xs">
            <div className="mb-1 text-[hsl(var(--kb-muted))] uppercase tracking-wider">Message body</div>
            <textarea
              className="kb-input"
              rows={3}
              maxLength={400}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="reengage-body-input"
            />
          </label>
          <label className="block text-xs">
            <div className="mb-1 text-[hsl(var(--kb-muted))] uppercase tracking-wider">Deep link on tap</div>
            <input
              className="kb-input font-mono"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="/shop?sort=new"
              data-testid="reengage-url-input"
            />
          </label>
        </div>

        <DialogFooter className="mt-2 gap-2">
          <button
            type="button"
            className="kb-btn-outline"
            onClick={() => onOpenChange(false)}
            data-testid="reengage-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            className="kb-btn-primary inline-flex items-center gap-2"
            onClick={send}
            disabled={sending || previewCount === 0}
            data-testid="reengage-send-btn"
          >
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {sending ? "Sending…" : "Send broadcast"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
